import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAui } from "@assistant-ui/react";
import { submitToolCallResult } from "@/components/assistant-ui/tools/ask-submit";
import { useApprovalStore } from "@/components/custom/stores/approvalStore";
import { usePdfDocumentStore } from "@/components/pdf/pdfDocumentStore";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { Button } from "@/components/ui/button";
import { ClientLogger } from "@/lib/clientLogger";
import { appEvents } from "@/lib/events";
import { toast } from "sonner";
import { FileText, Check, X } from "lucide-react";
import { openPreviewInSidebar } from "@/lib/threadDocumentOps";

/**
 * Inline approval card rendered in the thread message where the `request_commit_approval`
 * tool call appeared. Replaces the old sidebar-based `ApprovalActionsRow`.
 *
 * Shows:
 * - Document title / summary
 * - "View Draft" button → opens the staged document in the sidebar (read-only preview)
 * - "Approve" button → same POST as the old sidebar row
 * - "Reject with comment" → expandable textarea + submit
 * - After resolution: shows "Approved ✓" or "Rejected — {reason}" (non-interactive)
 */
export const InlineApprovalCard = memo(function InlineApprovalCard({
  toolCallId,
}: {
  toolCallId: string;
}) {
  const { t } = useTranslation();
  const aui = useAui();
  const pendingByPreviewId = useApprovalStore((s) => s.pendingByPreviewId);
  const clearByPreviewId = useApprovalStore((s) => s.clearByPreviewId);

  // Find the pending entry that matches this toolCallId
  const pending = Object.values(pendingByPreviewId).find(
    (entry) => entry.toolCallId === toolCallId,
  );

  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resolved, setResolved] = useState<"approved" | "rejected" | null>(null);

  const onViewDraft = useCallback(() => {
    if (!pending) return;
    void openPreviewInSidebar(
      pending.previewId,
      pending.summary,
      pending.threadId,
      pending.generatedDocumentId,
    );
  }, [pending]);

  const onApprove = useCallback(async () => {
    if (!pending || submitting) return;
    setSubmitting(true);
    try {
      const outcome = await submitToolCallResult(pending.toolCallId, { approved: true });
      if (outcome.kind === "stale") {
        ClientLogger.warn("Approval POST returned stale (410)", {
          component: "InlineApprovalCard",
          metadata: { toolCallId: pending.toolCallId, previewId: pending.previewId },
        });
        toast.error(t("approval.stale_token"));
        clearByPreviewId(pending.previewId);
        setResolved("approved");
        return;
      }
      if (outcome.kind === "error") {
        ClientLogger.warn("Approval POST failed", {
          component: "InlineApprovalCard",
          metadata: { toolCallId: pending.toolCallId, status: outcome.status },
        });
        setSubmitting(false);
        return;
      }
      clearByPreviewId(pending.previewId);
      setResolved("approved");
      appEvents.emit("approval-success", {
        action: pending.action,
        documentName: pending.documentName,
        threadId: pending.threadId,
      });
    } catch (err) {
      ClientLogger.error("Approval POST threw", {
        error: err instanceof Error ? err : undefined,
        component: "InlineApprovalCard",
        metadata: { toolCallId: pending.toolCallId },
      });
      setSubmitting(false);
    }
  }, [pending, submitting, clearByPreviewId, t]);

  const onReject = useCallback(async () => {
    if (!pending || submitting) return;
    setSubmitting(true);
    try {
      const clamped = reason.trim().slice(0, 500);
      const payload = clamped ? { approved: false, reason: clamped } : { approved: false };
      const outcome = await submitToolCallResult(pending.toolCallId, payload);
      if (outcome.kind === "stale") {
        ClientLogger.warn("Rejection POST returned stale (410)", {
          component: "InlineApprovalCard",
          metadata: { toolCallId: pending.toolCallId, previewId: pending.previewId },
        });
        toast.error(t("approval.stale_token"));
        clearByPreviewId(pending.previewId);
        if (pending.generatedDocumentId) {
          useGeneratedDocumentStore.getState().removePendingVersion(pending.generatedDocumentId);
        }
        setResolved("rejected");
        return;
      }
      if (outcome.kind === "error") {
        ClientLogger.warn("Rejection POST failed", {
          component: "InlineApprovalCard",
          metadata: { toolCallId: pending.toolCallId, status: outcome.status },
        });
        setSubmitting(false);
        return;
      }
      clearByPreviewId(pending.previewId);
      if (pending.generatedDocumentId) {
        useGeneratedDocumentStore.getState().removePendingVersion(pending.generatedDocumentId);
      }
      setResolved("rejected");
      usePdfDocumentStore.getState().closeAllDocuments();
      const messageText = clamped
        ? t("approval.denied_with_reason", { reason: clamped })
        : t("approval.denied");
      aui.thread().append({ role: "user", content: [{ type: "text", text: messageText }] });
    } catch (err) {
      ClientLogger.error("Rejection POST threw", {
        error: err instanceof Error ? err : undefined,
        component: "InlineApprovalCard",
        metadata: { toolCallId: pending.toolCallId },
      });
      setSubmitting(false);
    }
  }, [pending, submitting, reason, clearByPreviewId, t, aui]);

  // After resolution: show a simple status badge
  if (resolved === "approved") {
    return (
      <div className="my-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
        <Check className="h-4 w-4" />
        <span>{t("approval.status.approved")}</span>
      </div>
    );
  }
  if (resolved === "rejected") {
    return (
      <div className="my-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        <X className="h-4 w-4" />
        <span>{reason.trim() ? t("approval.status.rejected_with_reason", { reason: reason.trim() }) : t("approval.status.rejected")}</span>
      </div>
    );
  }

  // No pending entry found (expired, consumed, or not yet hydrated)
  if (!pending) {
    ClientLogger.debug("InlineApprovalCard: no pending entry for toolCallId (store may not be stamped yet)", {
      component: "InlineApprovalCard",
      metadata: { toolCallId },
    });
    return null;
  }

  const promptText = pending.action === "save_revision" && pending.documentName
    ? t("approval.confirm_revision", { name: pending.documentName })
    : pending.action === "commit_version" && pending.documentName
      ? t("approval.confirm_new_version")
      : pending.summary?.trim() || t("approval.review_this_draft");

  return (
    <div className="my-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-800 dark:bg-amber-950/30" data-testid="inline-approval-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-md bg-amber-100 p-1.5 dark:bg-amber-900">
          <FileText className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">{promptText}</p>
          {pending.documentName && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{pending.documentName}</p>
          )}
        </div>
      </div>

      {!rejectOpen && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onViewDraft}
            className="text-xs"
          >
            <FileText className="h-3 w-3 mr-1" />
            {t("approval.view_draft")}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={submitting}
              onClick={() => setRejectOpen(true)}
              className="text-xs"
            >
              {t("approval.reject")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={submitting}
              onClick={onApprove}
              className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
            >
              {t("approval.approve")}
            </Button>
          </div>
        </div>
      )}

      {rejectOpen && (
        <div className="mt-3 flex flex-col gap-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("approval.reject_reason_placeholder")}
            maxLength={500}
            rows={2}
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            disabled={submitting}
            autoFocus
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={submitting}
              onClick={() => { setRejectOpen(false); setReason(""); }}
              className="text-xs"
            >
              {t("approval.cancel_reject")}
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={submitting}
              onClick={onReject}
              className="text-xs"
            >
              {t("approval.submit_reject")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
